/**
 * The query parameters that say where a visitor came from, and the rule for
 * carrying them across an internal redirect.
 *
 * First-touch attribution is read by Mixpanel from the URL of the FIRST page
 * that initialises it. A route that redirects before the app shell mounts
 * therefore decides whether a campaign is ever attributed: drop the query and
 * the visit is reported as direct, whatever the link said. `/signup` is exactly
 * such a route (it opens the desktop gate or the mobile welcome sheet rather
 * than a page of its own), and so is `/login` on desktop (it hands the visitor
 * to JuisciGPT). Every inbound campaign link to either depends on this.
 *
 * ONLY `utm_*` travels. A redirect that forwarded the whole query string would
 * take anything a future link put on those paths into the app shell, where
 * Mixpanel autocaptures the URL and records the session — which is the leak this
 * codebase already guards against elsewhere
 * (`.knowledge/mixpanel-capability-url-leak.md`). An allowlist cannot carry a
 * capability by accident; a passthrough can.
 */
const CAMPAIGN_PARAM_PREFIX = "utm_";

/**
 * The campaign half of a query string, as a `search` a redirect can pass on.
 *
 * Returns "" when the visit carries no campaign, which `<Redirect>` reads as
 * "no query" — so an ordinary visit is unchanged.
 */
export function campaignSearch(search: string): string {
  const campaign = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(search)) {
    if (key.startsWith(CAMPAIGN_PARAM_PREFIX)) campaign.append(key, value);
  }
  const serialized = campaign.toString();
  return serialized === "" ? "" : `?${serialized}`;
}
